import { Button, Grid, TextField, Typography } from '@mui/material';
import { makeStyles } from '@mui/styles';
import { useSnackbar } from 'notistack';

import { BaseNote } from './Types';
import { useEffect, useMemo, useState } from 'react';
import { LoadingButton } from '@mui/lab';
import { convertToStoreLocalTime } from '@/common/utils/time';
import { formDataInstance } from '@/common/axios';
import CONFIG from '@/config';
import { getRestHeaders } from '@/common/utils/api';
import { MIconButton } from '@/@material-extend';
import { getErrorDetails } from './ApiErrorLookup';
import CardLoader from '@/components/CardLoader';

const useStyles: any = makeStyles(() => ({
  container: {
    color: 'grey',
    backgroundColor: 'rgba(233, 0, 0, 0.05)',
    padding: '16px 12px',
    marginBottom: 12,
    borderRadius: 8
  },
  saveDetails: {
    color: '#666',
    fontSize: 12,
    fontWeight: 400,
    lineHeight: 1
  },
  safetyTitle: {
    color: '#E90000',
    marginTop: 6
  },
  content: {
    fontWeight: 400,
    margin: '8px 0',
    color: '#000',
    overflow: 'hidden'
  },
  loading: {
    margin: 'auto'
  },
  alertContent: {
    border: '1px solid #308CBF',
    '& .MuiOutlinedInput-notchedOutline:hover': {
      borderColor: '#308CBF'
    }
  },
  addBtn: {
    color: '#055577',
    paddingLeft: 0,
    marginTop: 8,
    width: 140,
    fontWeight: 600,
    cursor: 'pointer'
  },
  saveCancel: {
    marginTop: 12,
    display: 'flex',
    justifyContent: 'end',
    '& button:first-child': {
      backgroundColor: '#FAFAFA',
      borderColor: '#FAFAFA',
      color: '#000',
      marginRight: 8,
      '&:hover': {
        backgroundColor: '#FFF'
      }
    },
    '& button': {
      backgroundColor: '#055577',
      color: '#FFF',
      fontWeight: 600,
      '&:hover': {
        backgroundColor: '#042E43'
      }
    }
  }
}));

interface AddSafetyAlertResponse {
  data: {
    data: { success: boolean; safetyAlert: BaseNote };
  };
}

interface AddSafetyAlertParams {
  customerId: number;
  content: string;
}

export interface SafetyNoteItemProps {
  customerId: number;
}

export const apiAlertSuffix = '/staff/user-safety-alert';

export const SafetyNoteItem = (props: SafetyNoteItemProps) => {
  const { customerId } = props;

  const classes = useStyles();
  const { enqueueSnackbar, closeSnackbar } = useSnackbar();

  const [safetyAlert, setSafetyAlert] = useState<BaseNote>(null);
  const [nextAlertText, setNextAlertText] = useState<string>(
    (safetyAlert?.content as string) || ''
  );
  const [loading, setLoading] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [isSaving, setIsSaving] = useState(false);

  const alertExists = safetyAlert !== null;

  const saveDetails = useMemo(() => {
    if (alertExists) {
      return `Last Edited by ${safetyAlert.createdBy?.firstName} ${
        safetyAlert.createdBy?.lastName
      } on ${convertToStoreLocalTime(
        safetyAlert.createdAt,
        'MM/DD/yyyy'
      )} at ${convertToStoreLocalTime(safetyAlert.createdAt, 'h:mm A')}`;
    } else {
      return '';
    }
  }, [safetyAlert]);

  useEffect(() => {
    if (customerId) {
      setLoading(true);
      formDataInstance
        .get(
          `${CONFIG.USERS_API_URL}${apiAlertSuffix}?customerId=${customerId}`
        )
        .then((response: AddSafetyAlertResponse) =>
          setSafetyAlert(response.data.data.safetyAlert)
        )
        .catch(error =>
          enqueueSnackbar(
            getErrorDetails(`There was a problem getting Notes.`, error),
            {
              variant: 'error',
              action: (key: any) => (
                <MIconButton size="small" onClick={() => closeSnackbar(key)} />
              )
            }
          )
        )
        .finally(() => setLoading(false));
    }
  }, [customerId]);

  const saveAlert = () => {
    setIsSaving(true);

    const params: AddSafetyAlertParams = {
      customerId: customerId,
      content: nextAlertText
    };
    const addEditSuccessMsg = `Safety Alert ${
      alertExists ? 'saved' : 'added'
    } successfully`;

    const method = alertExists ? 'patch' : 'post';

    formDataInstance[method](
      `${CONFIG.USERS_API_URL}${apiAlertSuffix}`,
      params,
      {
        headers: getRestHeaders()
      }
    )
      .then((response: AddSafetyAlertResponse) => {
        if (response.data.data.success) {
          enqueueSnackbar(addEditSuccessMsg, {
            variant: 'success',
            action: (key: any) => (
              <MIconButton size="small" onClick={() => closeSnackbar(key)} />
            )
          });
          // update the record in the parent component
          setSafetyAlert(response.data.data.safetyAlert);
          setIsEditing(false);
        }
      })
      .catch(error => {
        enqueueSnackbar(
          getErrorDetails('There was a saving safety alert.', error),
          {
            variant: 'error',
            action: (key: any) => (
              <MIconButton size="small" onClick={() => closeSnackbar(key)} />
            )
          }
        );
      })
      .finally(() => setIsSaving(false));
  };

  const cancelAddEdit = () => {
    if (safetyAlert?.content) {
      setNextAlertText(safetyAlert.content as string);
    }
    setIsEditing(false);
    setIsSaving(false);
  };

  const handleKeyDown = e => {
    if (e.key === 'Escape') {
      cancelAddEdit();
    }
  };

  return (
    <Grid className={classes.container} container>
      {loading ? (
        <div className={classes.loading}>
          <CardLoader />
        </div>
      ) : (
        <>
          <Grid xs={12} item>
            {alertExists && (
              <Typography className={classes.saveDetails}>
                {saveDetails}
              </Typography>
            )}
            <Typography className={classes.safetyTitle}>
              Safety Alert
            </Typography>
            {alertExists && !isEditing && (
              <Typography className={classes.content}>
                {safetyAlert.content}
              </Typography>
            )}
            {isEditing && (
              <TextField
                fullWidth
                multiline
                id="alert-content"
                inputRef={input => input && input.focus()}
                onFocus={e =>
                  e.currentTarget.setSelectionRange(
                    e.currentTarget.value.length,
                    e.currentTarget.value.length
                  )
                }
                rows={3}
                name="note"
                inputProps={{
                  'data-testid': 'note'
                }}
                value={nextAlertText}
                onChange={e => setNextAlertText(e.target.value)}
                onKeyDown={handleKeyDown}
              />
            )}
          </Grid>
          <Grid xs={12} item>
            {!isEditing && (
              <div
                className={classes.addBtn}
                onClick={() => {
                  setIsEditing(true);
                  setNextAlertText(safetyAlert?.content as string);
                }}
              >
                {alertExists ? 'Edit' : 'Add'} Safety Alert
              </div>
            )}
            {isEditing && (
              <div className={classes.saveCancel} onClick={() => {}}>
                <Button variant="outlined" onClick={cancelAddEdit}>
                  Cancel
                </Button>
                <LoadingButton
                  variant="contained"
                  color="primary"
                  onClick={saveAlert}
                  loading={isSaving}
                >
                  Save
                </LoadingButton>
              </div>
            )}
          </Grid>
        </>
      )}
    </Grid>
  );
};
